import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import * as FaIcons from 'react-icons/fa';
import { contentAPI, servicesAPI } from '../services/api';
import { useSiteInfo } from '../contexts/SiteInfoContext';
import { useBooking } from '../contexts/BookingContext';
import HeroSection from '../components/layout/HeroSection';
import ServiceCard from '../components/services/ServiceCard';
import aboutHeroImage from '../assets/images/about-us.jpg';
import './About.css';

const { FaRoad, FaCheckCircle } = FaIcons;

const fallbackCapabilities = [
  {
    title: 'Comprehensive Servicing',
    description:
      'From first logbook appointment through to high-kilometre maintenance, we keep your vehicle performing at its best.',
    icon: 'FaWrench',
    points: [
      'Minor, major, and logbook servicing',
      'Customer concerns diagnosed before work proceeds',
      'Detailed reporting and recommendations',
    ],
  },
  {
    title: 'Systems & Safety',
    description:
      'Precision workmanship on critical systems ensures your vehicle responds exactly as it should every time you drive.',
    icon: 'FaCogs',
    points: [
      'Brake, clutch, and suspension repairs',
      'Drive belts, mounts, and driveline inspections',
      'Exhaust, ignition, and cooling system solutions',
    ],
  },
  {
    title: 'Comfort & Control',
    description:
      'Keep every journey comfortable and confident with targeted services for the conditions you rely on most.',
    icon: 'FaSnowflake',
    points: [
      'Air-conditioning servicing, diagnostics, and re-gassing',
      'Tyre supply, fitting, balancing, and 3D wheel alignment',
      'Electrical, battery health, and replacement services',
    ],
  },
];

const fallbackValues = [
  {
    icon: 'FaAward',
    title: 'Quality',
    description: 'We never compromise on workmanship or the parts we recommend for your vehicle.',
  },
  {
    icon: 'FaHeart',
    title: 'Honesty',
    description: 'Transparent pricing, clear communication, and approval before additional work.',
  },
  {
    icon: 'FaUsers',
    title: 'Customer Focus',
    description: 'We listen first, tailor solutions, and keep you informed from drop-off to pick-up.',
  },
  {
    icon: 'FaTools',
    title: 'Expertise',
    description: 'Highly skilled technicians experienced across all makes, models, and fleet requirements.',
  },
];

const fallbackStory = [
  `Automotive Care is a family-owned and operated motor mechanic workshop located in Laverton North. Since opening our doors in 2017, we have been dedicated to providing competitive, affordable servicing and repairs without compromising on workmanship.`,
  `Every vehicle we see is treated with the same level of detail and respect as if it were our own. That means honest communication, transparent pricing, and trusted advice before any additional work proceeds.`,
  `Our experienced team is always ready to assist with minor, major, and logbook servicing, brake and suspension work, diagnostics, inspections, air-conditioning maintenance, tyre solutions, and battery replacements.`,
];

const fallbackMetrics = [
  { label: 'Year established', value: '2017' },
  { label: 'Technicians', value: 'Experienced' },
  { label: 'Fleet clients', value: 'Growing' },
];

const getIconComponent = (iconName) => {
  if (iconName && FaIcons[iconName]) {
    return FaIcons[iconName];
  }
  return FaCheckCircle;
};

const About = () => {
  const { siteInfo } = useSiteInfo();
  const { openBookingModal } = useBooking();
  const [aboutPage, setAboutPage] = useState(null);
  const [services, setServices] = useState([]);
  const [featureValues, setFeatureValues] = useState([]);

  useEffect(() => {
    let isMounted = true;

    const loadContent = async () => {
      try {
        const [pageRes, servicesRes, featuresRes] = await Promise.all([
          contentAPI.getPage('about').catch(() => null),
          servicesAPI.getAllServices().catch(() => null),
          contentAPI.getFeatures().catch(() => null),
        ]);

        if (!isMounted) {
          return;
        }

        if (pageRes?.data) {
          setAboutPage(pageRes.data);
        }

        if (servicesRes?.data) {
          setServices(Array.isArray(servicesRes.data) ? servicesRes.data : servicesRes.data.results || []);
        }

        if (featuresRes?.data) {
          setFeatureValues(Array.isArray(featuresRes.data) ? featuresRes.data : featuresRes.data.results || []);
        }
      } catch (error) {
        console.error('Failed to load about page content', error);
      }
    };

    loadContent();

    return () => {
      isMounted = false;
    };
  }, []);

  const heroEyebrow =
    siteInfo?.site_settings?.['about.hero_eyebrow'] || 'Family-Owned Workshop in Laverton North';
  const heroTitle = aboutPage?.title || 'About Automotive Care';
  const heroLead =
    siteInfo?.site_settings?.['about.hero_lead'] ||
    'Established in 2017, Automotive Care exists to keep you safely on the road with quality vehicle servicing, transparent advice, and genuine customer care.';

  // Dynamic hero image with fallback to static (check for actual content, not just truthy value)
  const aboutHeroImageSrc = (aboutPage?.hero_image && aboutPage.hero_image.trim()) || aboutHeroImage;

  const storyParagraphs = aboutPage?.content
    ? aboutPage.content
        .split('</p>')
        .map((chunk) => chunk.replace(/<[^>]+>/g, '').trim())
        .filter(Boolean)
    : fallbackStory;

  const metrics = useMemo(() => {
    const configured = siteInfo?.site_settings?.['about.metrics'];
    if (Array.isArray(configured) && configured.length) {
      return configured;
    }
    return fallbackMetrics;
  }, [siteInfo]);

  const featuredServices = useMemo(() => {
    if (!services.length) {
      return [];
    }

    // Target service names to display
    const targetServices = [
      'minor service',
      'major service',
      'brakes, clutch & suspension'
    ];

    // Filter services to match target services
    const filtered = services.filter((service) =>
      targetServices.some((target) =>
        service.name.toLowerCase().includes(target) ||
        service.slug.toLowerCase().includes(target.replace(/[,\s&]+/g, '-'))
      )
    );

    // Sort them in the order specified
    return filtered.sort((a, b) => {
      const aIndex = targetServices.findIndex((target) =>
        a.name.toLowerCase().includes(target)
      );
      const bIndex = targetServices.findIndex((target) =>
        b.name.toLowerCase().includes(target)
      );
      return aIndex - bIndex;
    }).slice(0, 3);
  }, [services]);

  const valueCards = useMemo(() => {
    if (!featureValues.length) {
      return fallbackValues;
    }
    return featureValues.slice(0, 4).map((feature) => ({
      icon: feature.icon || 'FaCheckCircle',
      title: feature.title,
      description: feature.description,
    }));
  }, [featureValues]);

  return (
    <>
      <Helmet>
        <title>About Us - Automotive Care | Laverton North</title>
        <meta
          name="description"
          content="Automotive Care is a family-owned mechanic workshop in Laverton North offering quality vehicle servicing, repairs, and diagnostics at competitive prices."
        />
      </Helmet>

      <HeroSection
        badge={heroEyebrow}
        title={heroTitle}
        subtitle={heroLead}
        backgroundImage={aboutHeroImageSrc}
        showButtons={false}
      />

      <section className="section story-section">
        <div className="container">
          <div className="story-grid">
            <motion.div
              className="story-text"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2>Our Story</h2>
              {storyParagraphs.map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
              ))}
            </motion.div>

            <motion.div
              className="story-card"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="story-stat">
                <span className="stat-value">{metrics[0]?.value || '2017'}</span>
                <span className="stat-label">{metrics[0]?.label || 'Year established'}</span>
              </div>
              <div className="story-separator" />
              <div className="story-highlight">
                <FaRoad />
                <div>
                  <strong>Keeping your ride running on the road</strong>
                  <p>
                    Reliable workmanship, genuine customer service, and exceptional value - providing peace of mind every
                    drive.
                  </p>
                </div>
              </div>
              <div className="story-cta">
                <h3>What we stand for</h3>
                <ul>
                  <li>Integrity with every recommendation</li>
                  <li>Quality parts, tools, and diagnostic equipment</li>
                  <li>Work completed by experienced technicians</li>
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="section capabilities-section bg-gray">
        <div className="container">
          <motion.div
            className="section-header text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2>How We Help Drivers Every Day</h2>
            <p className="section-subtitle">
              A complete suite of services designed to maintain safety, performance, and comfort.
            </p>
          </motion.div>

          <div className="services-grid">
            {featuredServices.length > 0 ? (
              featuredServices.map((service, index) => {
                const Icon = getIconComponent(service.category?.icon);
                return (
                  <ServiceCard
                    key={service.id || service.slug}
                    category={service}
                    index={index}
                    onBookClick={openBookingModal}
                    Icon={Icon}
                  />
                );
              })
            ) : (
              // Fallback if no services match
              fallbackCapabilities.map((capability, index) => {
                const Icon = getIconComponent(capability.icon);
                return (
                  <motion.div
                    key={capability.title}
                    className="capability-card"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1, duration: 0.45 }}
                    whileHover={{ translateY: -10 }}
                  >
                    <div className="capability-icon">
                      <Icon />
                    </div>
                    <h3>{capability.title}</h3>
                    <p>{capability.description}</p>
                    {capability.points.length > 0 && (
                      <ul>
                        {capability.points.map((point) => (
                          <li key={point}>{point}</li>
                        ))}
                      </ul>
                    )}
                  </motion.div>
                );
              })
            )}
          </div>

          <div className="text-center" style={{ marginTop: '2.5rem' }}>
            <Link to="/services" className="btn btn-primary btn-lg">
              See All Services
            </Link>
          </div>
        </div>
      </section>

      <section className="section values-section">
        <div className="container">
          <div className="section-header text-center">
            <h2>Our Core Values</h2>
            <p className="section-subtitle">The promises we uphold in every interaction and service.</p>
          </div>

          <div className="values-grid">
            {valueCards.map((value, index) => {
              const Icon = getIconComponent(value.icon);
              return (
                <motion.div
                  key={value.title}
                  className="value-card"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.45 }}
                >
                  <div className="value-icon">
                    <Icon />
                  </div>
                  <h3>{value.title}</h3>
                  <p>{value.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="section commitment-section">
        <div className="container">
          <div className="commitment-card">
            <div className="commitment-text">
              <h2>We keep you informed at every step.</h2>
              <p>
                Before we lift a spanner, we take the time to discuss the best maintenance or repair approach for your vehicle
                and your budget. When additional work is recommended, you receive a clear report, pricing, and a plan - no
                surprises.
              </p>
            </div>
            <div className="commitment-services">
              <h3>Work we stand behind</h3>
              <div className="commitment-tags">
                <span>Vehicle inspections</span>
                <span>Diagnostics & reporting</span>
                <span>Mechanical repairs</span>
                <span>Aircon servicing</span>
                <span>Tyre & wheel alignment</span>
                <span>Battery replacement</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default About;
